import { useMemo } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { parseCurrencyPayload } from '@shared/components/TravelCurrencySelector/parseCurrencyPayload';
import { TRAVEL_DEFAULT_CURRENCY_CODES } from '@shared/components/TravelCurrencySelector/travelCurrencies';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';

type TravelCurrencyAllowlist = Readonly<{
  allCurrencyCodes: ReadonlyArray<CurrencyCodeType>;
  popularCurrencyCodes: ReadonlyArray<CurrencyCodeType> | undefined;
  allowedSet: ReadonlySet<string>;
  loading: boolean;
}>;

type UseTravelCurrencyAllowlistArgs = Readonly<{
  /**
   * Skip the flag read, so no exposure fires where the answer is not used. Pass it on a surface
   * that only sometimes needs the list; the default list is returned meanwhile.
   */
  skip?: boolean;
}>;

// Resolves the set of allowed travel currency codes from the travelCurrencies
// feature flag, falling back to the hardcoded default list.
// Uses the union of popularCurrencyCodes and allCurrencyCodes so that any
// currency visible in the picker is also accepted by API-layer validation.
export const useTravelCurrencyAllowlist = ({ skip = false }: UseTravelCurrencyAllowlistArgs = {}): TravelCurrencyAllowlist => {
  const [, isFlagLoading, , flagPayload] = useIsFeatureEnabled('travelCurrencies', { skip });
  // A skipped read never settles, so it must not report loading — that is an answer, not a wait.
  const isFFLoading = !skip && isFlagLoading;

  return useMemo(() => {
    if (isFFLoading) {
      return {
        allCurrencyCodes: TRAVEL_DEFAULT_CURRENCY_CODES,
        popularCurrencyCodes: undefined,
        allowedSet: new Set<string>(TRAVEL_DEFAULT_CURRENCY_CODES),
        loading: true,
      };
    }
    const payload = parseCurrencyPayload(flagPayload);
    if (!payload?.allCurrencyCodes && !payload?.popularCurrencyCodes) {
      return {
        allCurrencyCodes: TRAVEL_DEFAULT_CURRENCY_CODES,
        popularCurrencyCodes: undefined,
        allowedSet: new Set<string>(TRAVEL_DEFAULT_CURRENCY_CODES),
        loading: false,
      };
    }

    const payloadAllCodes = payload.allCurrencyCodes ?? TRAVEL_DEFAULT_CURRENCY_CODES;
    const merged = new Set<string>(payloadAllCodes);
    if (payload.popularCurrencyCodes) {
      for (const code of payload.popularCurrencyCodes) {
        merged.add(code);
      }
    }

    return {
      allCurrencyCodes: payloadAllCodes,
      popularCurrencyCodes: payload.popularCurrencyCodes,
      allowedSet: merged,
      loading: false,
    };
  }, [isFFLoading, flagPayload]);
};
